import React, { useEffect, useState } from "react";
import { ANIMATIONS, BUTTON_TEXT, BACKGROUND_IMAGE_URL } from "../constants";
import { useGameAudio } from "../GameAudioProvider";

interface GameStartScreenProps {
  gameTitle: string;
  gameDescription?: string;
  onStartGame: () => void;
  onShowHowToPlay: () => void;
}

const GameStartScreen: React.FC<GameStartScreenProps> = ({
  gameTitle,
  gameDescription,
  onStartGame,
  onShowHowToPlay,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const fadeDuration = ANIMATIONS.FADE_DURATION;
  const { stopAudio, startBackgroundMusic } = useGameAudio();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleStartGame = () => {
    stopAudio();
    startBackgroundMusic();
    onStartGame();
  };

  const handleShowHowToPlay = () => {
    stopAudio();
    onShowHowToPlay();
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url(${BACKGROUND_IMAGE_URL})`,
      }}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-20 h-20 bg-white rounded-full animate-bounce delay-1000"></div>
        <div className="absolute top-32 right-20 w-16 h-16 bg-white rounded-full animate-bounce delay-2000"></div>
        <div className="absolute bottom-20 left-32 w-12 h-12 bg-white rounded-full animate-bounce delay-3000"></div>
        <div className="absolute bottom-40 right-16 w-24 h-24 bg-white rounded-full animate-bounce delay-500"></div>
      </div>

      <div
        className={`flex flex-col items-center justify-center min-h-screen px-4 transform transition-all ${
          isLoaded ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
        }`}
        style={{
          transitionDuration: `${fadeDuration}ms`,
        }}
      >
        <div className="text-center mb-8 w-full">
          <h1 
            className="font-black leading-tight drop-shadow-2xl animate-gradient-x animate-float px-4 bg-gradient-to-r from-red-500 via-cyan-400 via-blue-500 via-orange-400 via-teal-400 via-yellow-400 via-purple-500 to-sky-400 bg-clip-text text-transparent"
            style={{
              fontSize: 'clamp(2rem, 10vw, 6rem)',
              backgroundSize: '200% 200%',
              textShadow: '0 0 40px rgba(255,255,255,0.5)',
            }}
          >
            {gameTitle}
          </h1>

          {gameDescription && (
            <p 
              className="text-white/95 mx-auto leading-relaxed mt-4 px-6 font-medium line-clamp-2"
              style={{
                fontSize: 'clamp(0.875rem, 2.5vw, 1.125rem)',
                maxWidth: '90%',
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
              }}
            >
              {gameDescription}
            </p>
          )}
        </div>

        <div className="space-y-6 w-full max-w-md mt-8">
          <button
            onClick={handleStartGame}
            className="w-full py-4 px-8 text-xl font-bold text-white bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 border-4 border-white/20 hover:border-white/40 active:scale-95"
          >
            {BUTTON_TEXT.START_GAME}
          </button>

          <button
            onClick={handleShowHowToPlay}
            className="w-full py-4 px-8 text-lg font-semibold text-white bg-white/20 backdrop-blur-sm rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 border-2 border-white/30 hover:border-white/50 hover:bg-white/30 active:scale-95"
          >
            {BUTTON_TEXT.HOW_TO_PLAY}
          </button>
        </div>

        <div className="mt-16 flex space-x-4 animate-bounce">
          <span className="text-4xl">🌟</span>
          <span className="text-4xl">✨</span>
          <span className="text-4xl">🎉</span>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0">
        <svg
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          className="w-full h-20 fill-white/10"
        >
          <path d="M0,60 C150,120 350,0 600,60 C850,120 1050,0 1200,60 L1200,120 L0,120 Z"></path>
        </svg>
      </div>
    </div>
  );
};

export type { GameStartScreenProps };
export { GameStartScreen };
export default GameStartScreen;